import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { PopulatedEntitySuggestion } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';

interface ProposedEntityDetailsProps {
  suggestion: PopulatedEntitySuggestion;
}

/**
 * Read-only summary of the proposed entity's descriptive fields, shown on the
 * review step so the approver can tell what the suggestion actually is before
 * deciding to merge or create. The name lives in the modal header; ownership
 * (owner / lead / members) is editable in the create dialog and omitted here.
 */
export function ProposedEntityDetails({ suggestion }: ProposedEntityDetailsProps) {
  const { t } = useTranslation();
  const Icon = ENTITY_ICONS[suggestion.kind];

  const nameRow = {
    label: t('entitySuggestion.review.proposedDetails.name'),
    value: suggestion.payload.name
  };
  const rows =
    suggestion.kind === 'system'
      ? [
          nameRow,
          {
            label: t('entitySuggestion.review.proposedDetails.category'),
            value: suggestion.payload.category
          },
          {
            label: t('entitySuggestion.review.proposedDetails.url'),
            value: suggestion.payload.url
          },
          {
            label: t('entitySuggestion.review.proposedDetails.description'),
            value: suggestion.payload.description
          }
        ]
      : [
          nameRow,
          // Kind is fixed at suggestion time and the approve dialog has no
          // kind control, so the reviewer must see it here to decide between
          // approving and reject-and-re-suggest.
          {
            label: t('entitySuggestion.review.proposedDetails.kind'),
            value: t(`organization.orgFunctions.kind.${suggestion.payload.kind}` as const)
          },
          {
            label: t('entitySuggestion.review.proposedDetails.department'),
            value: suggestion.payload.department?.name
          },
          {
            label: t('entitySuggestion.review.proposedDetails.email'),
            value: suggestion.payload.email
          },
          {
            label: t('entitySuggestion.review.proposedDetails.description'),
            value: suggestion.payload.description
          }
        ];

  // The payload name is required, so there is always at least one visible row.
  const visibleRows = rows.filter((row) => Boolean(row.value));

  return (
    <div className="flex flex-col gap-3 rounded-md border p-4">
      <div className="flex items-center gap-2 border-b pb-3">
        <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-md">
          <Icon className="text-muted-foreground h-4 w-4" />
        </div>
        <span className="text-sm font-medium">
          {t(
            suggestion.kind === 'system'
              ? 'entitySuggestion.review.proposedDetails.headingSystem'
              : 'entitySuggestion.review.proposedDetails.headingTeam'
          )}
        </span>
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-2 text-sm">
        {visibleRows.map((row) => (
          <Fragment key={row.label}>
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className="break-words">{row.value}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}
